import httpService from "@src/utility/httpService";
import { ENDPOINTS } from "./apiUrlConstants";
import { config } from '@uhg-abyss/web/tools/config';
import { checkIsInternal } from "@src/utility/createStorage";
import { handleError } from "@src/utility/commonUtilityServices";

const _httpService = new httpService();
const SITE_NOTIFICATIONS_CACHE_KEY = 'siteNotifications';
let siteNotificationsRequest: Promise<any[]> | null = null;

if (checkIsInternal()) {
  _httpService.setBaseURL(config('API_DOMAIN_INTERNAL'));
} else {
  _httpService.setBaseURL(config('API_DOMAIN_EXTERNAL'));
}

// Banner interface
export interface NotificationBanner {
  id?: number;
  bannerType: string;
  internalExternal: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  locations: string[];
  content: string;
  status: string;
  createdBy?: string;
  createdDate?: string;
}
// Utility functions to process journey data
export const processJourneyData = (response) => {
  try {
    if (response.StatusCode === 200) {
      const respData = response.Data;
      const mainJourneys = respData.filter((v) => v.PrimaryId == null);
      const allJourneys: any = [];

      mainJourneys.forEach((mainJourney, i) => {
        allJourneys.push(mainJourney);
        const subJourney = getSubJourney(respData, mainJourney);

        if (subJourney.length) {
          addSubJourneyChildren(respData, subJourney);
          allJourneys[i].children = subJourney;
        }
      });

      return [...allJourneys].sort((a, b) => (a.JourneyId > b.JourneyId ? 1 : -1));
    } else {
      return [];
    }
  } catch (error) {
    console.error('Error processing journey data:', error);
    return [];
  }
};

export const getSubJourney = (respData, mainJourney) => {
  return respData
    .filter((a) => a.PrimaryId === mainJourney.JourneyId)
    .sort((a, b) => (a.JourneyId > b.JourneyId ? 1 : -1));
};

export const getSuperSubJourneys = (respData, element) => {
  return respData
    .filter((v) => v.PrimaryId === element.JourneyId)
    .sort((a, b) => (a.JourneyId > b.JourneyId ? 1 : -1));
};

export const addSubJourneyChildren = (respData, subJourney) => {
  subJourney.forEach((element, j) => {
    const superSubJourneys = getSuperSubJourneys(respData, element);
    if (superSubJourneys.length) {
      subJourney[j].children = superSubJourneys;
    }
  });
};
// Fetch all notification banners
export const fetchNotificationBanners = (): Promise<any> => {
  return new Promise(async (resolve, reject) => {
    try {
      const response: any = await _httpService.get(ENDPOINTS.JourneyDetails);
      const processedData = processJourneyData(response);
      resolve(processedData);
    } catch (e) {
      handleError('Error in fetchNotificationBanners', e);
      reject(new Error('Failed to fetch notification banners. Please try again later.'));
    }
  });
};
const transformBanner = (bannerObj) => {
  // Helper function to format date as MM/DD/YYYY using local time
  const formatDateOnly = (dateString) => {
    const date = new Date(dateString);
    // Use local time methods
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Helper function to format time as "10:15 PM" using local time
  const formatTimeOnly = (dateString) => {
    const date = new Date(dateString);
    // Use local time methods
    const hours24 = date.getHours();
    const minutes = date.getMinutes();

    // Convert to 12-hour format
    let hours12 = hours24;
    let ampm = 'AM';

    if (hours24 === 0) {
      hours12 = 12;
    } else if (hours24 > 12) {
      hours12 = hours24 - 12;
      ampm = 'PM';
    } else if (hours24 === 12) {
      ampm = 'PM';
    }

    return `${hours12}:${String(minutes).padStart(2, '0')} ${ampm}`;
  };

  // Helper function to format date and time for display using local time
  const formatDateTimeForDisplay = (dateString) => {
    const date = new Date(dateString);

    // Use local time methods for date
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2); // Get last 2 digits

    // Use local time methods for time
    const hours24 = date.getHours();
    const minutes = date.getMinutes();

    // Convert to 12-hour format
    let hours12 = hours24;
    let ampm = 'AM';

    if (hours24 === 0) {
      hours12 = 12;
    } else if (hours24 > 12) {
      hours12 = hours24 - 12;
      ampm = 'PM';
    } else if (hours24 === 12) {
      ampm = 'PM';
    }

    return `${month}/${day}/${year}, ${hours12}:${String(minutes).padStart(2, '0')} ${ampm} CT`;
  };

  // Calculate status based on current Central Time and banner date range
  const getCurrentCentralTime = () => {
    const now = new Date();
    return new Date(now.toLocaleString("en-US", {timeZone: "America/Chicago"}));
  };

  let status = 'Inactive';
  const today = getCurrentCentralTime();
  const startDate = new Date(bannerObj.StartDate);
  const endDate = new Date(bannerObj.EndDate);

  if (startDate <= today && endDate >= today) {
    status = 'Active';
  }

  return {
      ...bannerObj,
      CreatedDt: formatCTDate(bannerObj.CreatedDt, false, true),
      interDuration: `${formatDateTimeForDisplay(bannerObj.StartDate)} - ${formatDateTimeForDisplay(bannerObj.EndDate)}`,
      Status: status,
      // Add 4 new fields
      selectedStartDate: formatDateOnly(bannerObj.StartDate),
      selectedStartTime: formatTimeOnly(bannerObj.StartDate),
      selectedEndDate: formatDateOnly(bannerObj.EndDate),
      selectedEndTime: formatTimeOnly(bannerObj.EndDate)
  };
}
 const formatCTDate = (dateStr, withTime = true, createdBy = false) => {
  const date = new Date(dateStr);
  const options: any = {
      timeZone: "America/Chicago",
      year: "2-digit",
      month: "numeric",
      day: "numeric",
      hour: withTime ? "numeric" : undefined,
      minute: withTime ? "2-digit" : undefined,
      hour12: true,
  };
  return date.toLocaleString("en-US", options) + (withTime ? " CT" : "") + (createdBy ? " CT" : "");
}

const getCachedSiteNotifications = (): any[] | null => {
  try {
    const cachedNotifications = sessionStorage.getItem(SITE_NOTIFICATIONS_CACHE_KEY);
    if (!cachedNotifications) {
      return null;
    }

    const parsedNotifications = JSON.parse(cachedNotifications);
    return Array.isArray(parsedNotifications) ? parsedNotifications : null;
  } catch {
    sessionStorage.removeItem(SITE_NOTIFICATIONS_CACHE_KEY);
    return null;
  }
};

// Fetch site notifications
export const fetchSiteNotifications = (): Promise<any> => {
  const cachedNotifications = getCachedSiteNotifications();
  if (cachedNotifications) {
    return Promise.resolve(cachedNotifications);
  }

  if (siteNotificationsRequest) {
    return siteNotificationsRequest;
  }

  siteNotificationsRequest = new Promise(async (resolve, reject) => {
    try {
      const response: any = await _httpService.get(ENDPOINTS.siteNotifications);
      const updatedResponse = response?.Data?.map(transformBanner) || [];
      sessionStorage.setItem(SITE_NOTIFICATIONS_CACHE_KEY, JSON.stringify(updatedResponse));
      resolve(updatedResponse);
    } catch (e) {
      handleError('Error in fetchSiteNotifications', e);
      reject(new Error('Failed to fetch site notifications. Please try again later.'));
    } finally {
      siteNotificationsRequest = null;
    }
  });

  return siteNotificationsRequest;
};

// Save site notification
export const saveSiteNotification = (payload: any): Promise<any> => {
  return new Promise(async (resolve, reject) => {
    try {
      const response: any = await _httpService.push(ENDPOINTS.siteSaveNotifications, payload);
      sessionStorage.removeItem(SITE_NOTIFICATIONS_CACHE_KEY);
      resolve(response);
    } catch (e) {
      handleError('Error in saveSiteNotification', e);
      reject(new Error('Failed to save site notification. Please try again later.'));
    }
  });
};